// src/components/services/AtalPensionYojana.jsx
import React from "react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom'; // Import Link
import {
    MdOutlineSecurity,
    MdOutlineDescription,
    MdOutlineVerifiedUser,
    MdOutlinePayments,
    MdSavings
} from "react-icons/md";
import {
    FaPhoneAlt,
    FaHandHoldingUsd,
    FaUserShield,
    FaClock,
    FaLandmark,
} from "react-icons/fa";


function AtalPensionYojana() {
    const serviceName = "Atal Pension Yojana"; // Set service name

    // Animation Variants (Keep, or move to a separate file)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2, // Stagger animation of child elements
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <section className="py-16 md:py-24 px-4 md:px-8 bg-gray-50 text-gray-700">
            <div className="container mx-auto">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariants}
                >
                    <motion.h1
                        variants={itemVariants}
                        className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 md:mb-8 text-center"
                    >
                        Atal Pension Yojana (APY)
                    </motion.h1>
                    <motion.p
                        variants={itemVariants}
                        className="text-lg md:text-xl text-gray-600 mb-10 md:mb-12 text-center max-w-3xl mx-auto"
                    >
                        Secure your future with the Atal Pension Yojana (APY), a
                        government-backed pension scheme that provides a guaranteed minimum
                        monthly pension after retirement. Keziah Global Private Limited, in
                        partnership with Fino Payments Bank, makes it easy for you to enroll
                        in APY and start saving for your golden years.
                    </motion.p>

                    {/* Features and Benefits */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Key Features
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {/* Feature 1 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaLandmark className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Government-Backed Scheme
                                    </h4>
                                    <p className="text-gray-600">
                                        APY is a social security scheme launched by the Government of
                                        India, ensuring reliability and trust.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 2 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.1 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdSavings className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Guaranteed Pension
                                    </h4>
                                    <p className="text-gray-600">
                                        Receive a fixed minimum monthly pension of ₹1,000, ₹2,000,
                                        ₹3,000, ₹4,000, or ₹5,000 after the age of 60, depending on
                                        your contributions.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 3 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.2 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaHandHoldingUsd className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Affordable Contributions
                                    </h4>
                                    <p className="text-gray-600">
                                        Contribute a small amount monthly, starting as low as ₹42,
                                        depending on your age and desired pension amount.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 4 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.3 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <MdOutlineSecurity className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Tax Benefits
                                    </h4>
                                    <p className="text-gray-600">
                                        Get tax benefits on your contributions under Section 80CCD(1)
                                        of the Income Tax Act.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 5 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.4 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaUserShield className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Nomination Facility
                                    </h4>
                                    <p className="text-gray-600">
                                        Nominate a beneficiary to receive the pension in case of
                                        your unfortunate demise.
                                    </p>
                                </div>
                            </motion.div>
                            {/* Feature 6 */}
                            <motion.div
                                className="flex items-start space-x-4"
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2, delay: 0.5 }}
                            >
                                <div className="p-3 rounded-full bg-blue-100">
                                    <FaClock className="text-blue-600 text-2xl" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-lg">
                                        Long-Term Savings
                                    </h4>
                                    <p className="text-gray-600">
                                        Build a substantial retirement corpus over time with regular
                                        contributions.
                                    </p>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Eligibility and How to Enroll */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Eligibility and How to Enroll
                        </h3>
                        <p className="text-gray-600 text-lg mb-4">
                            Eligibility:
                        </p>
                        <ul className="list-disc pl-8 space-y-4 text-gray-600 text-lg mb-6">
                            <li>
                                Any Indian citizen between the ages of 18 and 40 years can enroll
                                in APY.
                            </li>
                            <li>
                                You must have a valid savings bank account with a participating
                                bank.
                            </li>
                        </ul>
                        <p className="text-gray-600 text-lg mb-4">
                            How to Enroll:
                        </p>
                        <ol className="list-decimal pl-8 space-y-4 text-gray-600 text-lg">
                            <li>
                                Visit our branch in Salem, Tamil Nadu, and our representatives
                                will assist you with the enrollment process.
                            </li>
                            <li>
                                Fill out the APY application form and provide the necessary KYC
                                documents.
                            </li>
                            <li>
                               Link your savings account to your APY account for
                                auto-debiting of contributions.
                            </li>
                        </ol>
                    </motion.div>

                    {/* Call to Action */}
                    <motion.div
                        variants={itemVariants}
                        className="mb-12 md:mb-16 bg-white rounded-xl shadow-md p-6 md:p-10"
                    >
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Start Saving for Your Future with APY Today!
                        </h3>
                        <p className="text-gray-600 text-lg text-center">
                            Visit our branch in Salem, Tamil Nadu, to enroll in the Atal
                            Pension Yojana and take a step towards a secure retirement. You can
                            also request a callback to learn more about the scheme and how it
                            can benefit you.
                        </p>
                        <motion.div
                            variants={itemVariants}
                            className="mt-10 flex flex-col md:flex-row justify-center space-y-4 md:space-y-0 md:space-x-6"
                        >
                            <a
                                href="tel:9043588446"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                whileHover={{ scale: 1.05 }}
                            >
                                <FaPhoneAlt className="inline-block mr-2" />
                                Call Now
                            </a>
                            {/* Use Link for Callback */}
                            <Link to={`/callback?serviceName=${encodeURIComponent(serviceName)}`}>
                                <motion.button
                                    className="bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full text-base md:text-lg transition duration-300 ease-in-out shadow-md hover:shadow-lg text-center w-full md:w-auto flex items-center justify-center"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    Request a Callback
                                </motion.button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}

export default AtalPensionYojana;